import type { TFunction } from 'i18next';

/**
 * Bucketed relative-time formatter — deliberately NOT `Intl.RelativeTimeFormat`.
 *
 * `Intl.RelativeTimeFormat`'s Somali (`so`) data is unreliable and silently
 * falls back to English in most engines, which would ship English strings into
 * a Somali UI without failing any test. So the bucket LABEL comes from i18n
 * (`vehicles.time.*`, real Somali in `so.json`); this module only decides the
 * bucket and the count. No locale data lives here.
 *
 * Bucketing (floor-based so tier transitions never produce "0 h" / "60 min"):
 *   elapsed < 45 s              → just now
 *   45 s … < 60 min            → {n} min   (n = max(1, floor(sec/60)))
 *   60 min … < 24 h            → {n} h     (n = floor(sec/3600), always ≥ 1)
 *   ≥ 24 h                      → {n} d     (n = floor(sec/86400), always ≥ 1)
 *
 * Boundaries pinned by test: 44 s → just now, 46 s → 1 min, 90 min → 1 h,
 * 25 h → 1 d. A future timestamp (clock skew) clamps to "just now".
 */

export type RelativeBucketKey = 'justNow' | 'minutes' | 'hours' | 'days';

export interface RelativeBucket {
  key: RelativeBucketKey;
  /** 0 for `justNow`; otherwise the unit count to interpolate. */
  count: number;
}

const SECOND = 1000;
const MINUTE = 60;
const HOUR = 3600;
const DAY = 86400;

/**
 * The single bucketing rule, in seconds. Both entry points below feed it, so a
 * threshold change moves once and stays consistent across the timestamp path and
 * the server-computed-age path.
 */
export function relativeAgeBucket(ageSec: number): RelativeBucket {
  // Clock skew or a slightly-future fix reads as "just now", never negative.
  const elapsedSec = Math.max(0, Math.floor(ageSec));

  if (elapsedSec < 45) return { key: 'justNow', count: 0 };
  if (elapsedSec < HOUR) return { key: 'minutes', count: Math.max(1, Math.floor(elapsedSec / MINUTE)) };
  if (elapsedSec < DAY) return { key: 'hours', count: Math.floor(elapsedSec / HOUR) };
  return { key: 'days', count: Math.floor(elapsedSec / DAY) };
}

export function relativeTimeBucket(fromMs: number, nowMs: number): RelativeBucket {
  return relativeAgeBucket((nowMs - fromMs) / SECOND);
}

/**
 * Resolves the bucket to a localized string via i18n. `justNow` has no count;
 * the other buckets interpolate `{{count}}` — i18next falls back to the base
 * key (single grammatical Somali string per bucket), so no plural suffixes and
 * no silent English leak.
 */
export function formatRelativeTime(t: TFunction, fromMs: number, nowMs: number): string {
  return labelForBucket(t, relativeTimeBucket(fromMs, nowMs));
}

/**
 * Localizes a relative age the *server* already computed (seconds since the
 * event, on the server's clock). Preferred over `formatRelativeTime` wherever
 * the API ships an age, so the browser's own clock — often wrong on a cheap
 * Android phone — never drifts the number the customer reads.
 */
export function formatRelativeAge(t: TFunction, ageSec: number): string {
  return labelForBucket(t, relativeAgeBucket(ageSec));
}

function labelForBucket(t: TFunction, bucket: RelativeBucket): string {
  if (bucket.key === 'justNow') return t('vehicles.time.justNow');
  return t(`vehicles.time.${bucket.key}`, { count: bucket.count });
}

/**
 * A wall-clock time-of-day ("09:26") in the viewer's locale, for the freshness
 * line's "no new position since {time}". Absolute, not relative: unlike an age,
 * a wall-clock instant is unaffected by a wrong phone CLOCK — only by the phone's
 * timezone, which is what the user wants to read it in.
 *
 * `Intl.DateTimeFormat` numeric HH:MM is safe where `Intl.RelativeTimeFormat` was
 * not: the output is digits and a separator, not a locale word, so there is no
 * silent Somali→English fallback to leak. `hour12: false` pins the 24-hour shape
 * the copy shows regardless of the locale's default. Returns `null` for an
 * absent/unparseable timestamp so callers fall back rather than print "Invalid".
 */
export function formatClockTime(locale: string, iso: string | null): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
  try {
    return new Intl.DateTimeFormat(locale || 'en', opts).format(ms);
  } catch {
    return new Intl.DateTimeFormat('en', opts).format(ms);
  }
}

/** Same local calendar day, in the viewer's zone. Uses the browser's own
 *  date parts, so the "is it today" test agrees with the HH:MM below (both
 *  render in the machine zone, exactly as `formatClockTime` does). */
function isSameLocalDay(aMs: number, bMs: number): boolean {
  const a = new Date(aMs);
  const b = new Date(bMs);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * "Tracker silent since" time (13f): the wall-clock HH:MM when the instant falls
 * on the viewer's current day, and a short date + HH:MM when it does not — so a
 * tracker that went silent yesterday does not read as a time today. Absolute, in
 * the viewer's own zone and locale, the SAME choice `formatClockTime` makes for
 * the position-freshness sub-line, so a stale tracker's silent-since line and a
 * stale fix's last-position line speak in one voice on the same screen.
 *
 * Returns null for an absent/unparseable timestamp so callers render nothing
 * rather than "Invalid" — the honest sub-line is no line at all.
 */
export function formatSilentSince(
  locale: string,
  iso: string | null,
  nowMs: number = Date.now(),
): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const l = locale || 'en';
  const opts: Intl.DateTimeFormatOptions = isSameLocalDay(ms, nowMs)
    ? { hour: '2-digit', minute: '2-digit', hour12: false }
    : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false };
  try {
    return new Intl.DateTimeFormat(l, opts).format(ms);
  } catch {
    return new Intl.DateTimeFormat('en', opts).format(ms);
  }
}
